import React, { useState, useEffect, useRef } from "react";
import { StoryType } from "data/types";
import { getEnvVariables } from "helpers/getEnvVariables";

interface StoryPlayerProps {
  storiesList: StoryType[];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  startId: string;
}

const StoryPlayer: React.FC<StoryPlayerProps> = ({ storiesList, setIsOpen,startId }) => {
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRefs = useRef<HTMLVideoElement[]>([]);

  const { REACT_APP_URL } = getEnvVariables();

  // Agrupar historias por uploaderId
  const groupedStories = Object.values(
    storiesList.reduce((acc: { [key: string]: any }, story) => {
      const { _id: uploadBy, name, img: uploaderImg } = story.data;

      if (!acc[uploadBy]) {
        acc[uploadBy] = {
          uploaderId: uploadBy,
          uploaderName: name,
          uploaderImage: uploaderImg,
          stories: [],
        };
      }

      if (Array.isArray(story.img)) {
        story.img.forEach((storyItem) => {
          acc[uploadBy].stories.push({
            id: storyItem._id,
            type: storyItem.type,
            url: `${REACT_APP_URL}view/stories/${storyItem.img}`,
            time: storyItem.time,
            view: storyItem.view,
          });
        });
      }

      return acc;
    }, {})
  );
  useEffect(() => {
  setCurrentGroupIndex(groupedStories.findIndex((group) => group.uploaderId === startId));
  }, []);

  const currentGroup = groupedStories[currentGroupIndex] || {};
  const currentStories = currentGroup.stories || [];

  useEffect(() => {
    if (currentStories.length === 0) return;

    const currentStory = currentStories[currentStoryIndex];
    let interval: NodeJS.Timeout;

    if (currentStory?.type === "imagen") {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            handleNextStory();
            return 0;
          }
          return prev + 100 / (currentStory.time || 5);
        });
      }, 1000);
    } else if (currentStory?.type === "video" && videoRefs.current[currentStoryIndex]) {
      const video = videoRefs.current[currentStoryIndex];
      video.currentTime = 0;
      video.play().then(() => video.play()).catch((s) => {});
      const updateProgress = () => {
        setProgress((video.currentTime / video.duration) * 100);
        if (video.ended) handleNextStory();
      };
      video.addEventListener("timeupdate", updateProgress);

      return () => {
        video.pause();
        video.removeEventListener("timeupdate", updateProgress);
      };
    }

    return () => clearInterval(interval);
  }, [currentStoryIndex, currentGroupIndex]);

  const handleNextStory = () => {
    setProgress(0);
    if (currentStoryIndex < currentStories.length - 1) {
      setCurrentStoryIndex((prev) => prev + 1);
    } else if (currentGroupIndex < groupedStories.length - 1) {
      setCurrentGroupIndex((prev) => prev + 1);
      setCurrentStoryIndex(0);
    } else {
      setIsOpen(false); // Cerrar historias al final
    }
  };

  const handlePrevStory = () => {
    setProgress(0);
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex((prev) => prev - 1);
    } else if (currentGroupIndex > 0) {
      const prevGroupIndex = currentGroupIndex - 1;
      setCurrentGroupIndex(prevGroupIndex);
      setCurrentStoryIndex(groupedStories[prevGroupIndex].stories.length - 1);
    }
  };

  if (!currentStories[currentStoryIndex]) return null;

  return (
    <div className="relative flex items-center justify-center h-[80vh]">
      {/* Fondo desenfocado */}
      <div className="absolute inset-0 w-full h-full">
        {currentStories[currentStoryIndex]?.type === "imagen" ? (
          <img
            src={currentStories[currentStoryIndex]?.url}
            alt="Story background"
            className="absolute inset-0 w-full h-full object-cover blur-3xl"
          />
        ) : (
          <video
            key={groupedStories[currentGroupIndex].stories[currentStoryIndex].id}
            ref={(ref) => (videoRefs.current[currentStoryIndex] = ref!)}
            className="absolute inset-0 w-full h-full object-cover blur-3xl"
            playsInline
            muted
            loop
          >
            <source src={currentStories[currentStoryIndex]?.url} type="video/mp4" />
          </video>
        )}
      </div>

      {/* Barra de progreso */}
         <div className="absolute top-0 left-0 h-1 flex rounded-full w-full">
        {currentStories.map((s: any, index: number) => (
          <div
            key={index}
            className="w-full h-1 flex rounded-full bg-gray-700 mr-1"
            style={{ width: `${100 / currentStories.length}%` }}
          />
        ))}
      </div>

      {/* Barra de progreso para el grupo actual */}
      <div className="absolute top-0 left-0 w-full h-1 flex rounded-full"> 
        {currentStories.map((s:any, index:number) => (
          <div
            key={currentStories[index].id}
            className="h-full transition-all bg-white rounded-full mr-1"
            style={{
              width: `${100 / currentStories.length}%`,
              transform: index < currentStoryIndex ? "scaleX(1)" : index === currentStoryIndex ? `scaleX(${progress / 100})` : "scaleX(0)",
              opacity: index <= currentStoryIndex ? 1 : 0.3,
              transformOrigin: "left",
            }}
          />
        ))}
      </div>
      {/* Barra de progreso para el grupo actual */}
  {/* Contenido principal */}
  <div className="relative w-full sm:w-2/3 max-w-md">
        {currentStories[currentStoryIndex]?.type === "imagen" ? (
          <img
            src={currentStories[currentStoryIndex]?.url}
            alt={`Story ${currentStoryIndex + 1}`}
            className="w-full h-full object-cover rounded-2xl"
          />
        ) : (
          <video
            key={groupedStories[currentGroupIndex].stories[currentStoryIndex].id}
            ref={(ref) => {
              videoRefs.current[currentStoryIndex] = ref!;
            }}
            className="w-full h-full object-cover rounded-2xl"
            playsInline
            autoPlay
          >
            <source src={currentStories[currentStoryIndex]?.url} type="video/mp4" />
          </video>
        )}
      </div>

      <div className="absolute top-4 left-4 flex items-center space-x-2">
        {currentGroup && (
          <>
            <img
              src={currentGroup.uploaderImage}
              alt={`Uploader ${currentGroup.uploaderName}`}
              className="w-10 h-10 rounded-full object-cover border-2 border-white"
            />
            <span className="text-white text-lg font-semibold">
              {currentGroup.uploaderName}
            </span>
          </>
        )}
      </div>
      {/* Navegación */}
      <button
        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-2xl"
        onClick={handlePrevStory}
      >
        &lt;
      </button>
      <button
        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-2xl"
        onClick={handleNextStory}
      >
        &gt;
      </button>
    </div>
  );
};

export default StoryPlayer;
